import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import Plus from "@insite/mobius/Icons/Plus";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListProductImage from "@thos/Overrides/Widgets/ProductList/ProductListProductImage";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
});

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    addToListWrapper?: InjectableCss;
    addToListLink?: ProductAddToListLinkStyles;
    parentContainer: InjectableCss;
    infoPriceContainer: InjectableCss;
    stockAvailabilityGrid: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
            border-top: 5px solid ${getColor("secondary.main")};
            box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
            padding: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            margin: auto;
            padding: 10px 25px;
        `,
    },
    addToListWrapper: {
        css: css`
            text-align: center;
            padding-top: 10px;
        `,
    },
    addToListLink: {
        link: {
            typographyProps: {
                color: "primary.main",
                size: 15,
                transform: "uppercase",
                weight: "bold",
                css: css`
                    color: ${getColor("primary.main")} !important;
                `,
            },
            icon: {
                position: "right",
                iconProps: {
                    src: Plus,
                    size: 19,
                    color: "primary.main",
                    css: css`
                        color: ${getColor("primary.main")} !important;
                    `,
                },
            },
        },
    },
    parentContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
        `,
    },
    infoPriceContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    stockAvailabilityGrid: {
        css: css`
            > div {
                > div {
                    > div {
                        > [data-test-selector="stockAvailabilityStatus"] {
                            flex-direction: column;
                        }
                    }
                }
            }
        `,
    },
        sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    settingsCollection,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
}) => {
    return (
        <ProductListProductContext product={product} key={product.id}>
            <StyledWrapper {...styles.parentContainer}>
                <StyledWrapper {...styles.wrapper} data-test-selector={`productListProductCard${product.id}`}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <ProductListProductImage
                            showImage={showImage}
                            showCompare={showCompare}
                            showImageBadge={showImageBadges}
                            showTextBadge={showTextBadges}
                            showImageBadges={showImageBadges}
                            showTextBadges={showTextBadges}
                            maxTextBadges={maximumNumberOfTextBadges}
                            maxImageBadges={maximumNumberOfImageBadges}
                        />
                    </StyledWrapper>
                    <ProductListProductInformation
                        showAttributes={showAttributes}
                        showAvailability={showAvailability}
                        showBrand={showBrand}
                        showPartNumbers={showPartNumbers}
                        showTitle={showTitle}
                        showImageBadges={showImageBadges}
                        maxImageBadges={maximumNumberOfImageBadges}
                        showTextBadges={showTextBadges}
                        maxTextBadges={maximumNumberOfTextBadges}
                    />
                    <StyledWrapper {...styles.stockAvailabilityGrid}>
                        <ProductContextAvailability />
                    </StyledWrapper>
                    <StyledWrapper {...styles.infoPriceContainer}>
                        <ProductListActions showAddToList={false} showPrice={showPrice} />
                    </StyledWrapper>
                </StyledWrapper>
                {showAddToList && (
                    <StyledWrapper {...styles.addToListWrapper}>
                        <ProductAddToListLink
                            data-test-selector={`actionsAddToList${product.id}`}
                            extendedStyles={styles.addToListLink}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </ProductListProductContext>
    );
};

export default connect(mapStateToProps)(withProduct(ProductListProductGridCard));
